import { useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useFlowstackOptional } from '@flowstack/sdk';
import { usePrivy } from '@privy-io/react-auth';
import ResumeModal from '../../components/session/ResumeModal';
import { WEB_BUILDER_ENABLED } from '../lib/flags';

const SAGE_API = import.meta.env.VITE_SAGE_API_URL || 'https://sage-api.flowstack.fun';

/** Attempt a silent JWT refresh. Returns the new token string on success, null on failure. */
async function silentRefresh(expiredToken: string): Promise<string | null> {
  try {
    const resp = await fetch(`${SAGE_API}/auth/token/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${expiredToken}` },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.token ?? null;
  } catch {
    return null;
  }
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const ctx = useFlowstackOptional();
  const navigate = useNavigate();
  const { logout: privyLogout } = usePrivy();
  const refreshingRef = useRef(false);

  // Redirect to login when not authenticated
  useEffect(() => {
    if (ctx?.isInitialized && !ctx.isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [ctx?.isInitialized, ctx?.isAuthenticated, navigate]);

  // Global 401 handler — attempt silent token refresh before forcing logout.
  // P0-72: Flowstack-only clear left Privy's sticky session in place.
  // P0-fix: 1-hour JWT TTL caused mid-session logouts; refresh endpoint extends to 8h.
  useEffect(() => {
    async function handle401(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail?.status !== 401) return;

      // Dedupe: if a refresh is already in-flight, don't kick off another
      if (refreshingRef.current) return;
      refreshingRef.current = true;

      try {
        // Try to get the current token from SDK credentials
        const currentToken = (ctx?.credentials as any)?.apiKey;
        if (currentToken) {
          const newToken = await silentRefresh(currentToken);
          if (newToken) {
            // Update credentials in the SDK context — prevents logout
            (ctx as any)?.updateCredentials?.({ apiKey: newToken });
            // Also update localStorage if the SDK persists there
            try {
              const stored = localStorage.getItem('flowstack:credentials');
              if (stored) {
                const parsed = JSON.parse(stored);
                parsed.apiKey = newToken;
                localStorage.setItem('flowstack:credentials', JSON.stringify(parsed));
              }
            } catch { /* ignore storage errors */ }
            refreshingRef.current = false;
            return; // Successfully refreshed — do NOT logout
          }
        }
      } catch { /* fall through to logout */ }

      refreshingRef.current = false;

      // Refresh failed or no token available — force full logout
      try {
        await privyLogout();
      } catch (err) {
        console.warn('[AuthGuard] Privy logout failed (may already be cleared):', err);
      }
      ctx?.logout?.();
      navigate('/', { replace: true });
    }
    window.addEventListener('flowstack:auth-error', handle401);
    return () => window.removeEventListener('flowstack:auth-error', handle401);
  }, [ctx, navigate, privyLogout]);

  if (!ctx?.isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--color-bg)]">
        <span className="w-6 h-6 rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)] spin" />
      </div>
    );
  }

  if (!ctx.isAuthenticated) return null;

  return <>{children}</>;
}

export default function WorkspaceLayout() {
  const navigate = useNavigate();
  return (
    <AuthGuard>
      <main>
        <Outlet />
        {/* P0-75 Phase 5: cross-device lock resolver. Only renders when
            another device holds the user's active session. Chat-session
            specific, so it disappears with the builder (P0-158). */}
        {WEB_BUILDER_ENABLED && (
          <ResumeModal
            onStartNew={() => navigate('/chat')}
            onResumeHere={(sessionId) =>
              navigate(`/chat?session_id=${encodeURIComponent(sessionId)}`)
            }
          />
        )}
      </main>
    </AuthGuard>
  );
}
